import React, { useEffect, useState, useRef, useContext } from "react";
import useClickOutside from "../../utils/CustomHooks/useClickOutside";
import { useOktaAuth } from "@okta/okta-react";
import { getRoles } from "../../utils/OktaHelpers";
import { LABSTORE_ACCESS, ROLES } from "../../utils/constants";
import {
    ACCESS_MANAGEMENT_ROUTES,
    DASHBOARD_ROUTES,
    USER_MENU_ROUTES,
} from "../../utils/RoutesConstant";
import { isAuthorized, isLabstoreAuthorized } from "../../utils/RolesHelpers";
import { ErrorAlert } from "../../utils/Alerts";
import LazyImage from "../Shared/LazyImage";
import NotificationCenter from "../Notifications/NotificationCenter";
import LogCapture from "../../utils/LogManagement/LogHelper";
import { useTranslation } from "react-i18next";
import SettingsMenu from "./SettingsMenu";
import lookupsAPI from "../../API/lookups.api";
import BaseAlertModal from "../Modals/BaseAlertModal";
import { clear, setItem } from "../../utils/StorageHelpers";
import ConditionalLink from "../Shared/ConditionalLink";
import { UserContext } from "../../lib/MainContext";
import NoAvatarImage from "../../assets/images/no-avatar.png";
import NeoSpectraLogo from "../../assets/images/logo5.png";
import { isEmpty } from "lodash";

const Navbar = ({ isRestricted = false }) => {
    const { user: currentUser, setUser, oktaClient } = useContext(UserContext);
    const [headerDropdownOpen, setHeaderDropdownOpen] = useState(false);
    const [notificationDropdownOpen, setNotificationDropdownOpen] =
        useState(false);
    const [roles, setRoles] = useState([]);
    const { authState } = useOktaAuth();
    const [notifications, setNotifications] = useState([]);
    const [notificationsActive, setNotificationsActive] = useState(false);

    // User management states
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrganization, setSelectedOrganization] = useState();
    const [switchAccountsModal, setSwitchAccountsModal] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const orgToBeChanged = useRef(null);
    const { t } = useTranslation();

    useEffect(() => {
        setRoles(getRoles());
        if (!isEmpty(currentUser) && authState?.isAuthenticated) {
            getOrganizations();
        }

        return () => {
            setRoles([]);
            setOrganizations([]);
        };
    }, [currentUser]);

    useEffect(() => {
        if (headerDropdownOpen) setNotificationDropdownOpen(false);
    }, [headerDropdownOpen]);

    useEffect(() => {
        if (notificationDropdownOpen) setHeaderDropdownOpen(false);
    }, [notificationDropdownOpen]);

    const toggleHeaderDropdown = () => {
        setHeaderDropdownOpen(!headerDropdownOpen);
    };

    const menuRef = useClickOutside(() => {
        setHeaderDropdownOpen(false);
        setNotificationDropdownOpen(false);
    });

    const getOrganizations = async () => {
        const result = await lookupsAPI.getUserOrganizations();
        if (result) {
            setOrganizations(result);
        }
    };

    const logout = async () => {
        try {
            LogCapture.info(
                `${currentUser?.email || "unknown"} logged out from the portal`
            );
            setIsLoggingOut(true);

            oktaClient.tokenManager.clear();
            oktaClient?.session?.close();

            setUser({});
            setIsLoggingOut(false);
            setHeaderDropdownOpen(false);
        } catch (error) {
            LogCapture.error(
                `${currentUser?.email || "unknown"} couldn't logout correctly`
            );
            ErrorAlert("Couldn't logout correctly!");
        } finally {
            setIsLoggingOut(false);
        }
    };

    const handleOrganizationSwitch = () => {
        try {
            setItem("user", {
                ...currentUser,
                organization: {
                    ...currentUser.organization,
                    id: orgToBeChanged?.current?.value,
                },
                role: orgToBeChanged?.current?.role,
            });
            setSelectedOrganization(orgToBeChanged.current);
            setSwitchAccountsModal(false);
            clear(true);
            window.location.replace(DASHBOARD_ROUTES.Overview);
        } catch {
            (error) => {
                console.log(error);
            };
        }
    };

    const renderSwitchAccountsModalContent = () => {
        return (
            <>
                <div className="card-flex flex py-2 px-2 items-baseline">
                    <div className="text-tiny text-primary-color-blue font-semibold">
                        {currentUser?.organization?.name}
                    </div>
                    <div className="mx-2">
                        <span className="fas fa-arrow-right text-xs text-primary-color-orange"></span>
                    </div>
                    <div className="text-tiny text-primary-color-blue font-semibold">
                        {orgToBeChanged?.current?.name}
                    </div>
                </div>
            </>
        );
    };

    const options = [
        {
            group: "settings",
            type: "link",
            horizontalLine: true,
            items: [
                {
                    name: t("profile"),
                    path: USER_MENU_ROUTES.Profile,
                    icon: "fas fa-user",
                    hide: isRestricted,
                },
                {
                    name: t("security"),
                    path: USER_MENU_ROUTES.Security,
                    icon: "fas fa-shield-alt",
                    hide: isRestricted,
                },
                {
                    name: t("affiliateProfile"),
                    path: ACCESS_MANAGEMENT_ROUTES.AffiliateProfile,
                    roles: [ROLES.partnerAdmin, ROLES.support, ROLES.admin],
                    icon: "fas fa-building",
                    hide:
                        !isAuthorized(
                            [ROLES.partnerAdmin, ROLES.support, ROLES.admin],
                            roles
                        ) || isRestricted,
                },
                {
                    name: t("subscriptionHistory"),
                    path: USER_MENU_ROUTES.SubscriptionHistory,
                    roles: [ROLES.partnerAdmin, ROLES.support],
                    hide:
                        !isAuthorized(
                            [ROLES.partnerAdmin, ROLES.support],
                            roles
                        ) ||
                        isRestricted ||
                        !isLabstoreAuthorized(
                            currentUser,
                            LABSTORE_ACCESS.view
                        ),
                    icon: "fas fa-list-ul",
                },
                {
                    name: isLoggingOut ? `${t("loggingOut")}...` : t("logout"),
                    disabled: isLoggingOut,
                    callback: logout,
                    icon: "fas fa-sign-out-alt",
                },
            ],
        },
        {
            group: "personalInformation",
            type: "info",
            horizontalLine: organizations.length > 1,
            items: [
                {
                    label: "email",
                    value: currentUser?.email || t("unknown"),
                    icon: "fa fa-envelope",
                },
                {
                    label: "role",
                    value: currentUser?.role || t("unknown"),
                    icon: "fas fa-user-cog",
                },
            ],
        },
        {
            group: "switchAccounts",
            type: "account",
            hide: organizations?.length === 1,
            items: organizations,
            defaultValue: {
                label: currentUser?.organization?.name,
                value: currentUser?.organization?.id,
                role: currentUser?.role,
            },
        },
    ];

    const getUserInfo = () => {
        const isValid = currentUser?.firstname && currentUser?.lastname;
        return (
            <div className="nav-user-data text-left pr-2 pt-1 flex-col gap-2">
                <div className="whitespace-nowrap">
                    <h6 className="font-semibold inline-block text-sm capitalize">
                        {isValid
                            ? `${currentUser?.firstname} ${currentUser?.lastname}`
                            : currentUser?.role || ""}
                    </h6>
                    <i className="fas fa-caret-down ml-1 caret-mobile" />
                </div>
                {isValid && (
                    <p className="leading-3 capitalize m-0">
                        {currentUser?.organization?.name || "unknown"}
                    </p>
                )}
            </div>
        );
    };

    return (
        <>
            <header id="main-header" className="main-header" ref={menuRef}>
                {/* Add  is-warning-user in the classname if you want the error to show */}
                <div
                    id="general-warning-message"
                    className="general-warning-message flex items-center justify-center"
                >
                    <i className="fas fa-exclamation-triangle"></i>
                    <span className="ml-2">
                        Be aware that your session is expired! Please refresh
                        the page to be able to continue
                    </span>
                </div>
                <div className="header-items-wrapper">
                    <div className="flex items-center justify-between flex-1 w-full">
                        <ConditionalLink
                            to={DASHBOARD_ROUTES.Overview}
                            className="header-logo-wrapper"
                            condition={!isRestricted}
                        >
                            <img src={NeoSpectraLogo} />
                        </ConditionalLink>

                        <NotificationCenter
                            notificationDropdownOpen={notificationDropdownOpen}
                            setNotificationDropdownOpen={
                                setNotificationDropdownOpen
                            }
                            notifications={notifications}
                            setNotifications={setNotifications}
                            notificationsActive={notificationsActive}
                            setNotificationsActive={setNotificationsActive}
                            userEmail={currentUser?.email}
                        />
                    </div>
                    <div
                        className="header-menu-mobile cursor-pointer hidden"
                        data-element="main-items-header"
                        id="mobile-view"
                    >
                        <i
                            onClick={toggleHeaderDropdown}
                            className={`fas fa-align-center text-primary-blue ${
                                headerDropdownOpen
                                    ? "fa-times rotated-icon"
                                    : "fa-align-center"
                            }`}
                        ></i>
                    </div>
                    <div
                        id="main-items-header"
                        className={`main-items-header flex items-center justify-end ${
                            headerDropdownOpen ? "show" : ""
                        }`}
                    >
                        {/* <div className="header-search-wrapper mx-auto"></div>
                         */}
                        <div className="header-dropdown-wrapper">
                            <div
                                className="flex cursor-pointer"
                                onClick={toggleHeaderDropdown}
                            >
                                <div
                                    className="header-dropdown-btn"
                                    data-element="user-dropdown"
                                >
                                    <LazyImage
                                        src={
                                            currentUser?.image || NoAvatarImage
                                        }
                                        alt="avatar"
                                    />
                                </div>
                                {currentUser && getUserInfo()}
                            </div>
                            <SettingsMenu
                                menuOptions={options}
                                roles={roles}
                                headerDropdownOpen={headerDropdownOpen}
                                setHeaderDropdownOpen={setHeaderDropdownOpen}
                                selectedOrganization={selectedOrganization}
                                orgToBeChanged={orgToBeChanged}
                                setSwitchAccountsModal={setSwitchAccountsModal}
                            />
                        </div>
                    </div>
                </div>
            </header>
            <BaseAlertModal
                isVisible={switchAccountsModal}
                setIsVisible={setSwitchAccountsModal}
                title={t("switchAccounts")}
                message={t("switchAccountsMessage")}
                action={{ submitAction: handleOrganizationSwitch }}
                iconClassName="fas fa-random"
            >
                {renderSwitchAccountsModalContent()}
            </BaseAlertModal>
        </>
    );
};

export default Navbar;
